import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { useGameStore } from '../store/game.store.js';
import { useRoomStore } from '../store/room.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { GameHeader } from '../components/game/GameHeader.js';
import { LeftPanel } from '../components/game/LeftPanel.js';
import { CenterPanel } from '../components/game/CenterPanel.js';
import { RightPanel } from '../components/game/RightPanel.js';
import { ClaimBar } from '../components/game/ClaimBar.js';
import { PauseOverlay } from '../components/game/PauseOverlay.js';
import { WinnerAnnouncement } from '../components/game/WinnerAnnouncement.js';

export function LiveGameScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const { roomDetails } = useRoomStore();
  const navigate = useNavigate();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const {
    ticketGrid,
    gameStatus,
    calledNumbers,
    currentNumber,
    previousNumber,
    markedNumbers,
    prizes,
    activityLog,
    leaderboard,
    gameTimer,
    currentNumberTime,
    activeAnnouncement,
    reactions,
    startGame,
    stopGame,
    togglePause,
    markNumber,
    claimPrize,
    sendEmojiReaction,
    isOut,
    countdownValue,
  } = useGameStore();

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  useEffect(() => {
    if (gameStatus === 'completed') {
      navigate('/results');
    }
  }, [gameStatus, navigate]);

  // Start game loop when mounting
  useEffect(() => {
    startGame();
    return () => {
      stopGame();
    };
  }, [startGame, stopGame]);

  const handleLeave = () => {
    const confirmLeave = window.confirm('Are you sure you want to leave the active match?');
    if (confirmLeave) {
      stopGame();
      navigate('/home');
    }
  };

  const handleClaim = (prizeType: string) => {
    if (user) {
      claimPrize(prizeType, user.displayName);
    }
  };

  const emojis = ['👍', '🎉', '🔥', '🎱', '👑', '😮', '❤️', '👏'];

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col bg-slate-950 pb-24 overflow-y-auto scrollbar-thin">
      {/* 3D background grids & lighting */}
      <BackgroundEffects />

      {/* Header bar */}
      <GameHeader
        roomCode={roomDetails?.code || 'TAMB99'}
        gameTimer={gameTimer}
        gameStatus={gameStatus}
        onPauseToggle={togglePause}
        onLeave={handleLeave}
      />

      {/* Main landscape column layouts (minimal padding/gaps for maximum mobile viewport space) */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-2 md:gap-3 px-2 md:px-4 py-2 z-20 overflow-y-auto lg:max-h-[calc(100vh-130px)] lg:overflow-hidden pb-10">
        {/* Left Column: Number info (1/4 width) */}
        <div className="lg:col-span-1 h-full overflow-hidden">
          <LeftPanel
            currentNumber={currentNumber}
            previousNumber={previousNumber}
            calledNumbers={calledNumbers}
            activityLog={activityLog}
            currentNumberTime={currentNumberTime}
          />
        </div>

        {/* Center Column: Player Ticket Grid (2/4 width) */}
        <div className="lg:col-span-2 h-full overflow-hidden">
          <CenterPanel
            ticketGrid={ticketGrid}
            currentNumber={currentNumber}
            calledNumbers={calledNumbers}
            markedNumbers={markedNumbers}
            onMarkCell={markNumber}
            isOut={isOut}
          />
        </div>

        {/* Right Column: Prizes & Leaderboard (1/4 width) */}
        <div className="lg:col-span-1 h-full overflow-hidden">
          <RightPanel
            leaderboard={leaderboard}
            currentUser={user ? user.displayName : ''}
          />
        </div>
      </main>

      {/* Bottom Claim Action Bar */}
      <ClaimBar prizes={prizes} onClaim={handleClaim} />

      {/* Pause overlay covering all gameplay interactions */}
      <PauseOverlay isPaused={gameStatus === 'paused'} />

      {/* Winner Announcement Popup */}
      <WinnerAnnouncement activeAnnouncement={activeAnnouncement} />

      {/* Floating Emoji Picker & Reactions Popover at bottom right corner */}
      <div className="fixed bottom-20 right-4 z-[100] flex flex-col items-end select-none">
        
        {/* Hotstar-style Live Floating Emoji reactions layer anchored directly to the reaction button */}
        <div className="absolute bottom-14 right-2 w-24 h-[300px] pointer-events-none overflow-visible flex items-end justify-center select-none">
          <AnimatePresence>
            {reactions.map((r) => (
              <motion.div
                key={r.id}
                initial={{ y: 0, x: r.xOffset, scale: 0.6, opacity: 1 }}
                animate={{ y: -220, opacity: 0, scale: 1.4 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.8, ease: 'easeOut', delay: r.delay }}
                className="absolute text-3xl pointer-events-none select-none filter drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
              >
                {r.emoji}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 10 }}
              className="mb-2 bg-slate-900/90 backdrop-blur-md border border-white/10 rounded-2xl p-2.5 flex space-x-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
            >
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    sendEmojiReaction(emoji);
                  }}
                  className="text-2xl hover:scale-125 transition-transform duration-200 cursor-pointer p-1"
                >
                  {emoji}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating trigger circle button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          style={{
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
          }}
          className="w-12 h-12 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-600 border border-purple-400/30 flex items-center justify-center text-xl cursor-pointer hover:from-purple-500 hover:to-indigo-500 shadow-glow animate-bounce hover:animate-none"
        >
          {showEmojiPicker ? '❌' : '😊'}
        </motion.button>
      </div>

      {/* Fullscreen 3D Countdown Overlay */}
      <AnimatePresence mode="wait">
        {gameStatus === 'countdown' && countdownValue > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-slate-950/85 backdrop-blur-md select-none"
          >
            <motion.div
              key={countdownValue}
              initial={{ scale: 0.3, opacity: 0, rotate: -20 }}
              animate={{ scale: 1.2, opacity: 1, rotate: 0 }}
              exit={{ scale: 2, opacity: 0, rotate: 20 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="flex flex-col items-center justify-center"
            >
              <span className="text-[120px] md:text-[200px] font-display font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-300 via-purple-500 to-pink-500 drop-shadow-[0_10px_50px_rgba(168,85,247,0.5)]">
                {countdownValue}
              </span>
              <motion.span
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 0.8 }}
                transition={{ delay: 0.2 }}
                className="text-xs md:text-sm font-bold uppercase tracking-widest text-purple-300 animate-pulse mt-4"
              >
                Get Ready! Game is Starting...
              </motion.span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
export default LiveGameScreen;
